import { PropsWithChildren, useCallback, useMemo } from 'react';

import { useAiPageDiscussionMutation } from '../../../../../queries/useAiPageDiscussionMutation';
import { useGetChatConversationMessagesQuery } from '../../../../../queries/useGetChatConversationMessagesQuery';
import {
  DiscussionContext,
  DiscussionSendPayload,
} from '../context/DiscussionContext';

export type FigmaDiscussionProviderProps = PropsWithChildren<{
  shareToken?: string;
  reviewId?: string;
  pageId?: string;
  messageId?: string;
  enabled?: boolean;
}>;

export const FigmaDiscussionProvider = ({
  shareToken,
  reviewId,
  pageId,
  messageId,
  enabled = true,
  children,
}: FigmaDiscussionProviderProps) => {
  const isReady = !!(enabled && shareToken && reviewId && pageId && messageId);

  const { data: messages, isPending: isLoadingMessages } =
    useGetChatConversationMessagesQuery(shareToken, messageId, isReady);

  const { mutateAsync, isPending: isSending } = useAiPageDiscussionMutation(
    shareToken,
    reviewId,
    messageId
  );

  const handleSendQuestion = useCallback(
    async (payload: DiscussionSendPayload) => {
      if (!pageId) return;
      await mutateAsync({ body: payload, pageId });
    },
    [mutateAsync, pageId]
  );

  const value = useMemo(
    () => ({
      messages,
      isLoadingMessages,
      isSending,
      isReady,
      sendQuestion: handleSendQuestion,
    }),
    [handleSendQuestion, isLoadingMessages, isReady, isSending, messages]
  );

  return (
    <DiscussionContext.Provider value={value}>
      {children}
    </DiscussionContext.Provider>
  );
};
